/**
 * Visual progress indicator for project lifecycle statuses.
 *
 * Renders a horizontal bar that fills based on the current status
 * position within the six-step pipeline, with colour interpolation
 * from dark-blue (early) to green (finished).
 *
 * @module ProjectProgressBar
 */

import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, LinearProgress, Typography, Chip } from '@mui/material';
import { DARK_BLUE, GREEN } from '../theme';

/** Ordered list of project lifecycle statuses from start to completion. */
const STATUSES = [
  { key: 'submitted', label: 'Submitted' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'quote_sent', label: 'Quote' },
  { key: 'contract_signed', label: 'Contract Signed' },
  { key: 'poc', label: 'POC' },
  { key: 'refine', label: 'Refine' },
  { key: 'pre_delivered', label: 'Pre-delivered' },
  { key: 'finished', label: 'Finished' },
];

/** Convert a status key into a 0–100 progress percentage. */
function getProgressValue(status: string): number {
  const idx = STATUSES.findIndex((s) => s.key === status);
  if (idx < 0) return 0;
  return ((idx + 1) / STATUSES.length) * 100;
}

/** Interpolate between DARK_BLUE and GREEN based on current progress. */
function getProgressColor(status: string): string {
  const progress = getProgressValue(status) / 100;
  // Interpolate from DARK_BLUE to GREEN
  const r1 = 0, g1 = 42, b1 = 58;
  const r2 = 67, g2 = 176, b2 = 42;
  const r = Math.round(r1 + (r2 - r1) * progress);
  const g = Math.round(g1 + (g2 - g1) * progress);
  const b = Math.round(b1 + (b2 - b1) * progress);
  return `rgb(${r}, ${g}, ${b})`;
}

interface Props {
  status: string;
  showLabels?: boolean;
}

/** Horizontal progress bar with labelled status chips beneath it. */
export default function ProjectProgressBar({ status, showLabels = true }: Props) {
  const { t } = useTranslation();
  const progress = getProgressValue(status);
  const color = getProgressColor(status);
  const activeIndex = STATUSES.findIndex((s) => s.key === status);
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeChipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current && activeChipRef.current) {
      const container = scrollRef.current;
      const chip = activeChipRef.current;
      const scrollLeft = chip.offsetLeft - container.clientWidth / 2 + chip.offsetWidth / 2;
      container.scrollTo?.({ left: scrollLeft, behavior: 'smooth' });
    }
  }, [activeIndex]);

  return (
    <Box
      ref={scrollRef}
      sx={{
        width: '100%',
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      <Box sx={{ minWidth: { xs: 560, sm: '100%' } }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 12,
            borderRadius: 6,
            bgcolor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              borderRadius: 6,
              background: `linear-gradient(90deg, ${DARK_BLUE} 0%, ${color} 100%)`,
              transition: 'transform 0.6s ease',
            },
          }}
        />
        {showLabels && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 1,
              pb: 0.5,
            }}
          >
            {STATUSES.map((s, idx) => (
              <Chip
                key={s.key}
                ref={idx === activeIndex ? activeChipRef : undefined}
                label={t('status.' + s.key.replace('_', '-'))}
                size="small"
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: idx <= activeIndex ? 600 : 400,
                  bgcolor: idx <= activeIndex ? color : '#e0e0e0',
                  color: idx <= activeIndex ? 'white' : 'text.secondary',
                  transition: 'all 0.3s ease',
                  flexShrink: 0,
                  maxWidth: `${100 / STATUSES.length}%`,
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export { STATUSES, getProgressValue };
